import type { OnlineContract } from './adminContractApi';
import {
  displayValue,
  formatDateTime,
  getContractTypeTitle,
  getSignatureImageSrc,
  getSignatureText,
  getStatusLabel,
  hasCustomerSignature,
} from './adminContractUtils';
import './AdminContractDetail.scss';

export type AdminContractDetailProps = {
  contract: OnlineContract | null;
  loading: boolean;
  error: string | null;
  deletingId: number | null;
  onBack: () => void;
  onEdit: (row: OnlineContract) => void;
  onDelete: (row: OnlineContract) => void;
};

export default function AdminContractDetail({
  contract,
  loading,
  error,
  deletingId,
  onBack,
  onEdit,
  onDelete,
}: AdminContractDetailProps) {
  const signatureImageSrc = contract ? getSignatureImageSrc(contract.signature_image) : null;
  const signatureText = contract ? getSignatureText(contract.signature_name) : null;
  const hasSignature = contract ? hasCustomerSignature(contract) : false;

  return (
    <div className="admin-contract-detail">
      <header className="admin-contract-detail__header">
        <button
          type="button"
          className="admin-contract-detail__back-btn"
          onClick={onBack}
          disabled={loading || deletingId != null}
        >
          ← 목록으로
        </button>
        <div className="admin-contract-detail__head">
          <div>
            <h2 className="admin-contract-detail__title">계약 상세</h2>
            <p className="admin-contract-detail__subtitle">
              등록된 계약 정보와 서명 내역을 확인할 수 있습니다.
            </p>
          </div>
          {contract && (
            <div className="admin-contract-detail__actions">
              {contract.status !== 'signed' && (
                <button
                  type="button"
                  className="admin-contract-detail__action-btn"
                  onClick={() => onEdit(contract)}
                >
                  수정
                </button>
              )}
              <button
                type="button"
                className="admin-contract-detail__action-btn admin-contract-detail__action-btn--danger"
                onClick={() => onDelete(contract)}
                disabled={deletingId === contract.id}
              >
                {deletingId === contract.id ? '삭제 중' : '삭제'}
              </button>
            </div>
          )}
        </div>
      </header>

      {error && <p className="admin-contract-detail__error">{error}</p>}

      {loading ? (
        <p className="admin-contract-detail__loading">상세 정보를 불러오는 중…</p>
      ) : !contract ? (
        <p className="admin-contract-detail__empty">계약 정보를 찾을 수 없습니다.</p>
      ) : (
        <div className="admin-contract-detail__stack">
          <section className="admin-contract-detail__panel">
            <div className="admin-contract-detail__status-row">
              <h3 className="admin-contract-detail__section-title">기본 정보</h3>
              <span
                className={`admin-contract-detail__status admin-contract-detail__status--${
                  contract.status === 'signed' ? 'signed' : 'pending'
                }`}
              >
                {getStatusLabel(String(contract.status))}
              </span>
            </div>

            <dl className="admin-contract-detail__grid">
              <div className="admin-contract-detail__item">
                <dt>계약 유형</dt>
                <dd>{getContractTypeTitle(String(contract.contract_type))}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>계약명</dt>
                <dd>{displayValue(contract.contract_name)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>상품명</dt>
                <dd>{displayValue(contract.product_name)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>구축비</dt>
                <dd>{displayValue(contract.build_cost)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>구축기간</dt>
                <dd>{displayValue(contract.build_period)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>월 유지보수비</dt>
                <dd>{displayValue(contract.maintenance_fee)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>유지보수기간</dt>
                <dd>{displayValue(contract.maintenance_period)}</dd>
              </div>
              <div className="admin-contract-detail__item admin-contract-detail__item--wide">
                <dt>특이사항</dt>
                <dd className="admin-contract-detail__pre">
                  {displayValue(contract.special_notes)}
                </dd>
              </div>
            </dl>
          </section>

          <section className="admin-contract-detail__panel">
            <h3 className="admin-contract-detail__section-title">지불방법</h3>
            {contract.payment_terms?.length ? (
              <table className="admin-contract-detail__payment-table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th>금액</th>
                    <th>지급조건</th>
                  </tr>
                </thead>
                <tbody>
                  {contract.payment_terms.map((row, index) => (
                    <tr key={index}>
                      <td>{displayValue(row.category)}</td>
                      <td>{displayValue(row.amount)}</td>
                      <td>{displayValue(row.condition)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="admin-contract-detail__empty">등록된 지불방법이 없습니다.</p>
            )}
          </section>

          <section className="admin-contract-detail__panel">
            <h3 className="admin-contract-detail__section-title">고객(갑) 정보</h3>
            <dl className="admin-contract-detail__grid">
              <div className="admin-contract-detail__item">
                <dt>상호명</dt>
                <dd>{displayValue(contract.company_name)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>대표자</dt>
                <dd>{displayValue(contract.representative)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>사업자등록번호(주민번호)</dt>
                <dd>{displayValue(contract.business_number)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>연락처</dt>
                <dd>{displayValue(contract.phone)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>이메일</dt>
                <dd>{displayValue(contract.email)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>주소</dt>
                <dd>{displayValue(contract.address)}</dd>
              </div>
            </dl>
          </section>

          <section className="admin-contract-detail__panel">
            <h3 className="admin-contract-detail__section-title">공급자(을) 정보</h3>
            <dl className="admin-contract-detail__grid">
              <div className="admin-contract-detail__item">
                <dt>상호명</dt>
                <dd>{displayValue(contract.provider_company_name)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>대표자</dt>
                <dd>{displayValue(contract.provider_representative)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>사업자등록번호(주민번호)</dt>
                <dd>{displayValue(contract.provider_business_number)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>연락처</dt>
                <dd>{displayValue(contract.provider_contact)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>주소</dt>
                <dd>{displayValue(contract.provider_address)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>계좌번호</dt>
                <dd>{displayValue(contract.provider_account_number)}</dd>
              </div>
            </dl>
          </section>

          <section className="admin-contract-detail__panel">
            <h3 className="admin-contract-detail__section-title">서명 · 동의</h3>
            <dl className="admin-contract-detail__grid">
              <div className="admin-contract-detail__item">
                <dt>약관 동의</dt>
                <dd>{contract.terms_agreed ? '동의' : '미동의'}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>개인정보 동의</dt>
                <dd>{contract.privacy_agreed ? '동의' : '미동의'}</dd>
              </div>
            </dl>
          </section>

          <section className="admin-contract-detail__panel admin-contract-detail__panel--signature">
            <h3 className="admin-contract-detail__section-title">고객 서명</h3>
            {hasSignature ? (
              <div className="admin-contract-detail__signature-card">
                <div className="admin-contract-detail__signature-meta">
                  <div className="admin-contract-detail__signature-meta-item">
                    <span className="admin-contract-detail__signature-meta-label">서명자</span>
                    <span className="admin-contract-detail__signature-meta-value">
                      {displayValue(contract.signature_name)}
                    </span>
                  </div>
                  <div className="admin-contract-detail__signature-meta-item">
                    <span className="admin-contract-detail__signature-meta-label">서명일</span>
                    <span className="admin-contract-detail__signature-meta-value">
                      {formatDateTime(contract.signed_at)}
                    </span>
                  </div>
                </div>
                <div className="admin-contract-detail__signature-box">
                  {signatureImageSrc ? (
                    <img
                      src={signatureImageSrc}
                      alt={`${displayValue(contract.signature_name)} 서명`}
                      className="admin-contract-detail__signature-image"
                    />
                  ) : (
                    <p className="admin-contract-detail__signature-text">{signatureText}</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="admin-contract-detail__signature-empty">아직 고객 서명이 없습니다.</p>
            )}
          </section>

          <section className="admin-contract-detail__panel">
            <h3 className="admin-contract-detail__section-title">계약 약관</h3>
            <pre className="admin-contract-detail__terms">{displayValue(contract.terms_text)}</pre>
          </section>

          <section className="admin-contract-detail__panel">
            <h3 className="admin-contract-detail__section-title">등록 정보</h3>
            <dl className="admin-contract-detail__grid">
              <div className="admin-contract-detail__item">
                <dt>등록자</dt>
                <dd>{displayValue(contract.created_by_name)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>등록자 이메일</dt>
                <dd>{displayValue(contract.created_by_email)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>등록일</dt>
                <dd>{formatDateTime(contract.created_at)}</dd>
              </div>
              <div className="admin-contract-detail__item">
                <dt>수정일</dt>
                <dd>{formatDateTime(contract.updated_at)}</dd>
              </div>
            </dl>
          </section>
        </div>
      )}
    </div>
  );
}
